import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Search, RefreshCw, Archive, CheckCircle2, XCircle, Trash2, Phone, Clock, MessageSquare, X } from 'lucide-react';
import { API_URL } from '../../../utils/apiUrl';

const STATUSES = [
  { value: '', label: 'Tümü', color: 'neutral' },
  { value: 'new', label: 'Yeni', color: 'blue' },
  { value: 'read', label: 'Okundu', color: 'neutral' },
  { value: 'replied', label: 'Cevaplandı', color: 'emerald' },
  { value: 'archived', label: 'Arşivde', color: 'amber' },
];

const STATUS_LABEL = {
  new: 'Yeni', read: 'Okundu', replied: 'Cevaplandı', archived: 'Arşivde',
};

const STATUS_STYLE = {
  new: { bg: '#DBEAFE', color: '#1E3A8A' },
  read: { bg: '#F3F4F6', color: '#374151' },
  replied: { bg: '#D1FAE5', color: '#065F46' },
  archived: { bg: '#FEF3C7', color: '#78350F' },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.new;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider" style={{ background: s.bg, color: s.color }} data-testid={`status-badge-${status}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
};

const ContactMessagesManagement = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [selected, setSelected] = useState(null);
  const [detailNote, setDetailNote] = useState('');

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (statusFilter) params.status = statusFilter;
      if (searchQ.trim()) params.q = searchQ.trim();
      const { data } = await axios.get(`${API_URL}/api/admin/contact-messages`, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setUnread(data.unread || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mesajlar yüklenemedi');
    } finally { setLoading(false); }
  }, [statusFilter, searchQ, page, pageSize]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const openDetail = async (id) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/contact-messages/${id}`);
      setSelected(data);
      setDetailNote(data.admin_note || '');
      // refresh list to reflect auto-mark-as-read
      fetchMessages();
    } catch (err) { toast.error(err.response?.data?.detail || 'Mesaj açılamadı'); }
  };

  const patch = async (id, body, successMsg) => {
    try {
      const { data } = await axios.patch(`${API_URL}/api/admin/contact-messages/${id}`, body);
      if (successMsg) toast.success(successMsg);
      setSelected(data);
      fetchMessages();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Bu mesajı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/contact-messages/${id}`);
      toast.success('Mesaj silindi');
      setSelected(null);
      fetchMessages();
    } catch (err) { toast.error(err.response?.data?.detail || 'Silme başarısız'); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black tracking-tight flex items-center gap-2" data-testid="contact-messages-title">
            <Mail className="w-6 h-6" style={{ color: 'var(--green-main)' }} />
            İletişim Mesajları
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            {total} mesaj{unread > 0 && <> · <span className="font-bold" style={{ color: '#1E3A8A' }}>{unread} okunmamış</span></>}
          </p>
        </div>
        <button onClick={fetchMessages}
          className="border border-neutral-300 px-3 py-2 rounded-md text-xs font-bold flex items-center gap-1.5 hover:bg-neutral-100 transition"
          data-testid="refresh-messages-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Yenile
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5 flex-wrap" data-testid="messages-filters">
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map(s => (
            <button key={s.value || 'all'} onClick={() => { setStatusFilter(s.value); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${statusFilter === s.value ? 'bg-[#1C3B2A] text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}
              data-testid={`filter-status-${s.value || 'all'}`}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-md">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
            <input type="text" value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); fetchMessages(); } }}
              placeholder="Ad, e-posta, konu veya mesajda ara..."
              className="w-full border border-neutral-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#1C3B2A]"
              data-testid="messages-search-input" />
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="p-10 text-center text-neutral-400 text-sm">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-neutral-300 rounded-md p-10 text-center" data-testid="messages-empty">
          <MessageSquare className="w-8 h-8 text-neutral-300 mx-auto mb-3" />
          <p className="text-sm text-neutral-500">Henüz iletişim mesajı bulunmuyor.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="messages-list">
          {items.map(m => (
            <button key={m.id} onClick={() => openDetail(m.id)}
              className={`w-full text-left border rounded-md p-4 transition hover:shadow-sm ${!m.is_read ? 'border-blue-300 bg-blue-50/40' : 'border-neutral-200 bg-white'}`}
              data-testid={`message-row-${m.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-sm ${!m.is_read ? 'font-black' : 'font-bold'}`} style={{ color: 'var(--text-primary)' }}>{m.name}</span>
                    {!m.is_read && <span className="w-2 h-2 rounded-full bg-blue-500" aria-label="okunmamış" />}
                    <StatusBadge status={m.status} />
                  </div>
                  <div className="flex items-center gap-3 flex-wrap text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                    {m.email && <span className="truncate max-w-[220px]" title={m.email}>{m.email}</span>}
                    {m.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{m.phone}</span>}
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(m.created_at)}</span>
                  </div>
                  {m.subject && <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-secondary-strong)' }}>{m.subject}</p>}
                  <p className="text-sm truncate mt-0.5" style={{ color: 'var(--text-secondary)' }}>{m.message}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6" data-testid="messages-pagination">
          <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}
            className="px-3 py-1.5 border border-neutral-300 rounded text-xs font-bold disabled:opacity-40 hover:bg-neutral-100">Önceki</button>
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{page} / {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            className="px-3 py-1.5 border border-neutral-300 rounded text-xs font-bold disabled:opacity-40 hover:bg-neutral-100">Sonraki</button>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="message-detail-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between gap-3 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>{selected.name}</h3>
                <StatusBadge status={selected.status} />
              </div>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-neutral-100 rounded-md" data-testid="close-detail-btn">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {selected.email && (
                  <div>
                    <p className="text-xs font-black uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>E-posta</p>
                    <a href={`mailto:${selected.email}`} className="font-semibold hover:underline break-all" style={{ color: 'var(--green-main)' }}>{selected.email}</a>
                  </div>
                )}
                {selected.phone && (
                  <div>
                    <p className="text-xs font-black uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Telefon</p>
                    <a href={`tel:+90${selected.phone}`} className="font-semibold hover:underline" style={{ color: 'var(--green-main)' }}>+90 {selected.phone}</a>
                  </div>
                )}
                <div>
                  <p className="text-xs font-black uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Tarih</p>
                  <p className="font-medium" style={{ color: 'var(--text-secondary-strong)' }}>{formatDate(selected.created_at)}</p>
                </div>
                {selected.source_page && (
                  <div>
                    <p className="text-xs font-black uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Kaynak Sayfa</p>
                    <p className="text-xs truncate font-medium" style={{ color: 'var(--text-secondary)' }} title={selected.source_page}>{selected.source_page}</p>
                  </div>
                )}
              </div>

              {selected.subject && (
                <div>
                  <p className="text-xs font-black uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Konu</p>
                  <p className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{selected.subject}</p>
                </div>
              )}

              <div>
                <p className="text-xs font-black uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Mesaj</p>
                <div className="border border-neutral-200 rounded-md p-4 bg-neutral-50 whitespace-pre-wrap text-sm leading-relaxed" style={{ color: 'var(--text-secondary-strong)' }} data-testid="message-detail-body">
                  {selected.message}
                </div>
              </div>

              <div>
                <label className="text-xs font-black uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-muted)' }}>Yönetici Notu</label>
                <textarea value={detailNote} onChange={(e) => setDetailNote(e.target.value)} rows="2"
                  placeholder="Bu mesaj için not ekleyin (opsiyonel)..."
                  className="w-full border border-neutral-300 rounded-md p-2.5 text-sm focus:outline-none focus:border-[#1C3B2A]"
                  data-testid="admin-note-textarea" />
                <button onClick={() => patch(selected.id, { admin_note: detailNote }, 'Not kaydedildi')}
                  className="mt-2 text-xs font-bold hover:underline" style={{ color: 'var(--green-main)' }}
                  data-testid="save-note-btn">Notu Kaydet</button>
              </div>

              {selected.replied_at && (
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Cevaplandı: <span className="font-semibold">{formatDate(selected.replied_at)}</span>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-neutral-200 px-6 py-3 flex items-center gap-2 flex-wrap">
              <button onClick={() => patch(selected.id, { is_read: false }, 'Okunmadı olarak işaretlendi')}
                className="text-xs font-bold px-3 py-1.5 rounded border border-neutral-300 hover:bg-neutral-100 flex items-center gap-1.5"
                data-testid="mark-unread-btn">
                <XCircle className="w-3.5 h-3.5" /> Okunmadı
              </button>
              <button onClick={() => patch(selected.id, { is_read: true }, 'Okundu olarak işaretlendi')}
                className="text-xs font-bold px-3 py-1.5 rounded border border-neutral-300 hover:bg-neutral-100 flex items-center gap-1.5"
                data-testid="mark-read-btn">
                <CheckCircle2 className="w-3.5 h-3.5" /> Okundu
              </button>
              <button onClick={() => patch(selected.id, { status: 'replied' }, 'Cevaplandı olarak işaretlendi')}
                className="text-xs font-bold px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5"
                data-testid="mark-replied-btn">
                <CheckCircle2 className="w-3.5 h-3.5" /> Cevaplandı
              </button>
              <button onClick={() => patch(selected.id, { status: 'archived' }, 'Arşivlendi')}
                className="text-xs font-bold px-3 py-1.5 rounded border border-amber-400 text-amber-700 hover:bg-amber-50 flex items-center gap-1.5"
                data-testid="archive-btn">
                <Archive className="w-3.5 h-3.5" /> Arşivle
              </button>
              <div className="flex-1" />
              <button onClick={() => remove(selected.id)}
                className="text-xs font-bold px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1.5"
                data-testid="delete-btn">
                <Trash2 className="w-3.5 h-3.5" /> Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactMessagesManagement;
